import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import vconst from 'utils/helpers/structs/vconst';
import { createOffDateFilter } from 'modules/admin';
import { useAuth } from 'context/AuthContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { safeJSONParse } from 'utils/helpers/safeParsing';
import { useRegistryColumns } from 'utils/helpers/list/useRegistryColumns';

const ObjectContext = createContext();
export const useObject = () => useContext(ObjectContext);

const deObjecttatus = [
    vconst.objectInWork,
    vconst.objectPanding,
    vconst.objectFinishedTeam,
];

export { deObjecttatus as defaultObjectStatuses };

export const defaultSaleStatuses = [
    vconst.objectInWork,
];

const LEGACY_CLIENT_KEY = {
    object: 'objectListShowClient',
    sale: 'saleListShowClient',
    serviceSale: 'serviceSaleListShowClient',
};

const LEGACY_PAYMENTS_KEY = {
    object: 'objectListShowDebtor',
    sale: 'saleListShowDebtor',
    serviceSale: 'serviceSaleListShowDebtor',
};

export const ObjectProvider = ({ children, onlySale: onlySaleProp = false, onlyService: onlyServiceProp = false }) => {
    const { auth } = useAuth();
    const params = useSettingsParams();
    const onlyService = Boolean(onlyServiceProp);
    const onlySale = Boolean(onlySaleProp) && !onlyService;
    const isSaleLike = onlySale || onlyService;
    const prefix = onlyService ? 'serviceSale' : onlySale ? 'sale' : 'object';
    const columnDefs = useMemo(() => {
        const defs = [
            { id: 'client', defaultVisible: false, legacyKey: LEGACY_CLIENT_KEY[prefix] },
            { id: 'payments', defaultVisible: false, legacyKey: LEGACY_PAYMENTS_KEY[prefix] },
            { id: 'created', defaultVisible: false },
            { id: 'access', defaultVisible: false },
            { id: 'inWork', defaultVisible: false },
        ];
        if (!isSaleLike) defs.push({ id: 'team', defaultVisible: false });
        return defs;
    }, [prefix, isSaleLike]);
    const { visible: listColumns, toggle: toggleListColumn } = useRegistryColumns(
        `registryColumns:${prefix}`,
        columnDefs,
    );
    const showClient = Boolean(listColumns.client);
    const showTeam = Boolean(!isSaleLike && listColumns.team);
    const showDebtor = Boolean(params.objectPaymentControl && listColumns.payments);
    const showCreated = Boolean(listColumns.created);
    const showAccess = Boolean(listColumns.access);
    const showInWork = Boolean(listColumns.inWork);

    const filterName = `voltox-${auth.user.ID}-${prefix}-filter`;
    const filterTeamName = `voltox-${auth.user.ID}-${prefix}-filter-team`;
    const filterClientName = `voltox-${auth.user.ID}-${prefix}-filter-client`;
    const filterOverdueName = `voltox-${auth.user.ID}-object-filter-overdue`;
    const orderName = `voltox-${auth.user.ID}-${prefix}-order`;
    const descName = `voltox-${auth.user.ID}-${prefix}-desc`;

    const defaultStatuses = isSaleLike ? defaultSaleStatuses : deObjecttatus;

    const [objectsDateFilter, setObjectsDateFilter] = useState(createOffDateFilter);
    const [showSpinner, setShowSpinner] = useState(false);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const [objectsOrder, setObjectsOrder] = useState(
        safeJSONParse(sessionStorage.getItem(orderName)) || '',
    );
    const [objectsDesc, setObjectsDesc] = useState(
        safeJSONParse(sessionStorage.getItem(descName)) === true,
    );
    const [filterStatus, setFilterStatus] = useState(
        safeJSONParse(sessionStorage.getItem(filterName)) || defaultStatuses,
    );
    const [filterTeamId, setFilterTeamId] = useState(() => {
        const parsed = safeJSONParse(sessionStorage.getItem(filterTeamName));
        return parsed ? Number(parsed) || null : null;
    });
    const [filterClientId, setFilterClientId] = useState(() => {
        const parsed = safeJSONParse(sessionStorage.getItem(filterClientName));
        return parsed ? Number(parsed) || null : null;
    });
    const [filterOnlyOverdue, setFilterOnlyOverdue] = useState(
        () => !isSaleLike && safeJSONParse(sessionStorage.getItem(filterOverdueName)) === true,
    );
    useEffect(() => {
        sessionStorage.setItem(filterName, JSON.stringify(filterStatus));
    }, [filterStatus, filterName]);

    useEffect(() => {
        sessionStorage.setItem(filterTeamName, JSON.stringify(filterTeamId || null));
    }, [filterTeamId, filterTeamName]);

    useEffect(() => {
        sessionStorage.setItem(filterClientName, JSON.stringify(filterClientId || null));
    }, [filterClientId, filterClientName]);

    useEffect(() => {
        if (isSaleLike) return;
        sessionStorage.setItem(filterOverdueName, JSON.stringify(filterOnlyOverdue));
    }, [filterOnlyOverdue, filterOverdueName, isSaleLike]);

    useEffect(() => {
        sessionStorage.setItem(orderName, JSON.stringify(objectsOrder));
    }, [objectsOrder, orderName]);

    useEffect(() => {
        sessionStorage.setItem(descName, JSON.stringify(objectsDesc));
    }, [objectsDesc, descName]);

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);

    const values = {
        onlySale,
        onlyService,
        isSaleLike,
        showSpinner,
        setShowSpinner,
        objectsOrder,
        setObjectsOrder,
        objectsDesc,
        setObjectsDesc,
        filterStatus,
        setFilterStatus,
        objectsDateFilter,
        setObjectsDateFilter,
        filterTeamId,
        setFilterTeamId,
        filterClientId,
        setFilterClientId,
        filterOnlyOverdue,
        setFilterOnlyOverdue,
        showDebtor,
        showClient,
        showTeam,
        showCreated,
        showAccess,
        showInWork,
        toggleListColumn,
        listResetNonce,
        triggerListReset,
        listRefreshNonce,
        triggerListRefresh,
        defaultStatuses,
    };

    return <ObjectContext.Provider value={values}>{children}</ObjectContext.Provider>;
};
